import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { isSuper } from '~/src/dash/utils/roles';
import { isSamsungPublisher } from '~/src/dash/utils/ui';

export interface IDeviceEnvironment {
  key: string;
  text: string;
}

// TO BE DEPRECATED. PLEASE USE DEVICEOPTIONS IN THE TARGETINGPRESETS FILE INSTEAD.
export const deviceEnvironments: IDeviceEnvironment[] = [
  {
    key: '0',
    text: 'All',
  },
  {
    key: '1',
    text: 'Android Mobile',
  },
  {
    key: '2',
    text: 'Android Tablet',
  },
  {
    key: '3',
    text: 'Android TV',
  },
  {
    key: '7',
    text: 'Apple TV',
  },
  {
    key: '23',
    text: 'Bravia',
  },
  {
    key: '12',
    text: 'Chromecast',
  },
  {
    key: '16',
    text: 'Dish',
  },
  {
    key: '4',
    text: 'Fire TV',
  },
  {
    key: '17',
    text: 'Hisense',
  },
  {
    key: '20',
    text: 'Huawei',
  },
  {
    key: '5',
    text: 'iOS Mobile',
  },
  {
    key: '6',
    text: 'iOS Tablet',
  },
  {
    key: '15',
    text: 'LG',
  },
  {
    key: '18',
    text: 'Nvidia',
  },
  {
    key: '19',
    text: 'Phillips',
  },
  {
    key: '8',
    text: 'Playstation',
  },
  {
    key: '9',
    text: 'Roku',
  },
  {
    key: '11',
    text: 'Samsung TV',
  },
  {
    key: '21',
    text: 'Tcl',
  },
  {
    key: '22',
    text: 'Tivo',
  },
  {
    key: '14',
    text: 'Vizio',
  },
  {
    key: '10',
    text: 'Xbox',
  },
  {
    key: '13',
    text: 'Other',
  },
  {
    key: '24',
    text: 'Comcast',
  },
  {
    key: '25',
    text: 'Sharp',
  },
  {
    key: '26',
    text: 'Panasonic',
  },
  {
    key: '27',
    text: 'Sanyo',
  },
  {
    key: '28',
    text: 'Desktop',
  },
  {
    key: '29',
    text: 'AirTV',
  },
  {
    key: '30',
    text: 'Xiaomi',
  },
  {
    key: '31',
    text: 'AT&T',
  },
  {
    key: '32',
    text: 'Samsung Family Hub',
  },
];

export function getDeviceEnvironmentTextFromKey(key: string): string {
  return (
    deviceEnvironments.find((deviceEnv: IDeviceEnvironment) => deviceEnv.key === key) || {
      text: '',
    }
  ).text;
}

export function deviceEnvironmentsAsIMenu(): IMenuList {
  const out = {} as IMenuList;
  for (const d of deviceEnvironments) {
    out[d.key] = {
      selected: false,
      ID: d.key,
      title: d.text,
    } as IListItem;
  }

  return out;
}

function createDeviceEnvironmentsAsMap(): Map<number, string> {
  const out = new Map<number, string>();

  deviceEnvironments.forEach((dE) => {
    out.set(+dE.key, dE.text);
  });

  return out;
}

export const deviceEnvironmentsAsMap = createDeviceEnvironmentsAsMap();

export function getDeviceEnvironmentAsItems(deviceIDs: string) {
  const envList = deviceEnvironmentsAsIMenu();
  // Remove ALL
  delete envList['0'];

  const isAllSelected = deviceIDs.split(',').includes('0');
  // We want to remove the ALL option. Instead we select all other options + delete the ALL one

  // if All selected is returning

  // If ALL is selected, Select everything
  if (isAllSelected) {
    for (const idx of Object.values(envList)) {
      if (envList[idx.ID]) {
        envList[idx.ID].selected = true;
      }
    }
    return envList;
  }

  // Else only select selected options
  for (const selected of deviceIDs.split(',')) {
    if (envList[selected]) {
      envList[selected].selected = true;
    }
  }

  return envList;
}

// optional prop to disable items here

export function getDeviceEnvAsItemsAndDisable(deviceIDs: string, disabledIDs?: string[]) {
  const envList = deviceEnvironmentsAsIMenu();

  for (const selected of deviceIDs.split(',')) {
    if (envList[selected]) {
      envList[selected].selected = true;
    }
  }

  if ((disabledIDs || []).length) {
    for (const disabled of disabledIDs || []) {
      if (envList[disabled]) {
        envList[disabled].disabled = true;
      }
    }
  }

  return envList;
}

export enum DeviceSubType {
  DEVICESUBTYPE_UNKNOWN = '0',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V2_3 = '1',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V2_4 = '2',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V3_0 = '3',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V4_0 = '4',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V5_0 = '5',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V5_5 = '6',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V6_0 = '7',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V6_5 = '8',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V7_0 = '9',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V8_0 = '10',
  DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V9_0 = '11',
}

export interface IDeviceSubType {
  key: DeviceSubType;
  text: string;
}

export function getDeviceEnvironmentSubTypes(
  publisherID: string,
  isSuperUser: boolean,
): IDeviceSubType[] {
  let deviceEnvironmentSubTypes: IDeviceSubType[] = [];

  const samsungDeviceEnvironmentSubTypes: IDeviceSubType[] = [
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V2_3,
      text: 'Samsung Tizen 2.3',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V2_4,
      text: 'Samsung Tizen 2.4',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V3_0,
      text: 'Samsung Tizen 3.0',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V4_0,
      text: 'Samsung Tizen 4.0',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V5_0,
      text: 'Samsung Tizen 5.0',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V5_5,
      text: 'Samsung Tizen 5.5',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V6_0,
      text: 'Samsung Tizen 6.0',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V6_5,
      text: 'Samsung Tizen 6.5',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V7_0,
      text: 'Samsung Tizen 7.0',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V8_0,
      text: 'Samsung Tizen 8.0',
    },
    {
      key: DeviceSubType.DEVICESUBTYPE_TARGETING_SAMSUNGTIZEN_V9_0,
      text: 'Samsung Tizen 9.0',
    },
  ];

  // Keeps track of all non-default device environment subtypes
  const allDeviceEnvironmentSubTypeArrays: IDeviceSubType[][] = [samsungDeviceEnvironmentSubTypes];

  // when adding a new publisher make sure to delete it from
  // allDeviceEnvironmentSubTypeArrays after concat'ing it so
  // that the SuperUser logic below works
  if (isSamsungPublisher(publisherID)) {
    deviceEnvironmentSubTypes = deviceEnvironmentSubTypes.concat(samsungDeviceEnvironmentSubTypes);
    delete allDeviceEnvironmentSubTypeArrays[0];
  }

  // Add a * to all deviceEnvironment subtypes a SuperUser can see
  // that don't belong to the current publisher
  if (isSuperUser) {
    allDeviceEnvironmentSubTypeArrays.forEach((deviceEnvironmentSubTypeArray) => {
      deviceEnvironmentSubTypeArray.forEach((deviceEnvironmentSubType) => {
        deviceEnvironmentSubType.text = deviceEnvironmentSubType.text + ' *';
      });
      deviceEnvironmentSubTypes = deviceEnvironmentSubTypes.concat(deviceEnvironmentSubTypeArray);
    });
  }
  return deviceEnvironmentSubTypes;
}

export function deviceEnvironmentSubTypesAsIMenu(
  publisherID: string,
  isSuperUser: boolean,
): IMenuList {
  const out = {} as IMenuList;
  const deviceEnvironmentSubTypes = getDeviceEnvironmentSubTypes(publisherID, isSuperUser);
  for (const d of deviceEnvironmentSubTypes) {
    out[d.key] = {
      selected: false,
      ID: d.key,
      title: d.text,
    } as IListItem;
  }

  return out;
}

export function generateDeviceEnvironmentSubtypes(
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> {
  const out = {} as IMenuList;
  const deviceEnvironmentSubTypes = getDeviceEnvironmentSubTypes(
    session.publisher.ID,
    isSuper(user.user),
  );
  for (const d of deviceEnvironmentSubTypes) {
    out[d.key] = {
      selected: false,
      ID: d.key,
      title: d.text,
    } as IListItem;
  }

  return new Promise((resolve) => resolve({ 'Device Subtypes': out }));
}

export function getDeviceEnvironmentSubTypesAsItems(
  subTypeIDs: string,
  publisherID: string,
  isSuperUser: boolean,
) {
  const envList = deviceEnvironmentSubTypesAsIMenu(publisherID, isSuperUser);
  // Remove ALL
  delete envList['0'];

  const isAllSelected = subTypeIDs.split(',').includes('0');

  // If ALL is selected, Select everything
  if (isAllSelected) {
    for (const idx of Object.values(envList)) {
      if (envList[idx.ID]) {
        envList[idx.ID].selected = true;
      }
    }
    return envList;
  }

  // Else only select selected options
  for (const selected of subTypeIDs.split(',')) {
    if (envList[selected]) {
      envList[selected].selected = true;
    }
  }

  return envList;
}

export function getDeviceEnvSubTypesAsItemsAndDisable(
  deviceIDs: string,
  publisherID: string,
  isSuperUser: boolean,
  disabledIDs?: string[],
) {
  const envList = deviceEnvironmentSubTypesAsIMenu(publisherID, isSuperUser);

  for (const selected of deviceIDs.split(',')) {
    if (envList[selected]) {
      envList[selected].selected = true;
    }
  }

  if ((disabledIDs || []).length) {
    for (const disabled of disabledIDs || []) {
      if (envList[disabled]) {
        envList[disabled].disabled = true;
      }
    }
  }

  return envList;
}
